import { getSharedAudioContext } from "./sharedAudioContext";

/**
 * 録音中の音量を計測するモジュール。
 *
 * 文字起こしが失敗したとき、マイクが音を拾っていたかどうかをサーバ側のログで確定
 * させるために使う。外部の文字起こしサービスが音声ファイルを読めずに拒否した場合、
 * サーバ側では中身の音声を一切観測できないため、ブラウザ側の計測だけが無音を確定
 * できる（Issue #1438）。
 */

/** 音を検知したとみなす実効値の閾値。無音はほぼ 0、環境ノイズは 0.02 未満、発話は 0.05 以上になる。 */
export const VOICED_RMS_THRESHOLD = 0.02;

/** 計測の間隔（ミリ秒）。 */
export const SAMPLE_INTERVAL_MS = 100;

export interface RecordingLevelMetrics {
  /** 録音中の音量の最大値（0〜1）。 */
  peakLevel: number;
  /** 音を検知した秒数。 */
  voicedSeconds: number;
}

export interface RecordingLevelMeter {
  /** 計測を止め、接続したノードを切り離す（共有 AudioContext は破棄しない）。 */
  stop: () => void;
  /** 計測結果を返す。計測できなかった場合は null を返す。 */
  getMetrics: () => RecordingLevelMetrics | null;
}

const NOOP_METER: RecordingLevelMeter = {
  stop: () => {},
  getMetrics: () => null,
};

/**
 * 計測結果を送信データへ加える。計測できなかった場合は項目自体を送らず、サーバ側では
 * 「計測なし」と「無音」を区別できるようにする。
 */
export function appendAudioMetrics(
  formData: FormData,
  metrics: RecordingLevelMetrics | null
): void {
  if (!metrics) return;
  formData.append("audio_peak_level", String(metrics.peakLevel));
  formData.append("audio_voiced_seconds", String(metrics.voicedSeconds));
}

/**
 * 録音用のストリームの音量計測を開始する。音声解析を初期化できない環境では計測を
 * 諦めた meter を返す。計測は診断目的であり、失敗させて録音を止めてはならない。
 */
export function startRecordingLevelMeter(
  stream: MediaStream
): RecordingLevelMeter {
  const context = getSharedAudioContext();
  if (!context) return NOOP_METER;

  let source: MediaStreamAudioSourceNode;
  let analyser: AnalyserNode;
  try {
    source = context.createMediaStreamSource(stream);
    analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
  } catch {
    return NOOP_METER;
  }

  const waveform = new Uint8Array(analyser.fftSize);
  let peakLevel = 0;
  let voicedSampleCount = 0;

  const intervalId = setInterval(() => {
    analyser.getByteTimeDomainData(waveform);

    // 時間領域の波形（0〜255、無音は 128 付近）から実効値を求めて 0〜1 に正規化する。
    let sumOfSquares = 0;
    for (let i = 0; i < waveform.length; i++) {
      const amplitude = (waveform[i] - 128) / 128;
      sumOfSquares += amplitude * amplitude;
    }
    const rms = Math.sqrt(sumOfSquares / waveform.length);

    if (rms > peakLevel) peakLevel = rms;
    if (rms >= VOICED_RMS_THRESHOLD) voicedSampleCount += 1;
  }, SAMPLE_INTERVAL_MS);

  let stopped = false;

  return {
    stop() {
      if (stopped) return;
      stopped = true;
      clearInterval(intervalId);
      try {
        source.disconnect();
      } catch {
        // 切り離しに失敗しても計測結果は保持されるため、何もしない
      }
    },
    getMetrics() {
      return {
        peakLevel: Math.round(peakLevel * 1000) / 1000,
        voicedSeconds:
          Math.round(((voicedSampleCount * SAMPLE_INTERVAL_MS) / 1000) * 10) /
          10,
      };
    },
  };
}
